import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const ExcelUploader = () => {
  const [files, setFiles] = useState([]);

  // Function to handle file upload and read Excel file
  const handleFileUpload = (e, index) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = evt.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const sheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" }); // Keep empty fields as empty strings
      const headers = Object.keys(sheet[0]); // Extract headers from the first row

      const newFiles = [...files];
      newFiles[index] = { ...newFiles[index], columns: headers.map((header) => ({ label: header, value: header })), jsonData: sheet };
      setFiles(newFiles);
    };

    reader.readAsBinaryString(file);
    const newFiles = [...files];
    newFiles[index] = { ...newFiles[index], file: file };
    setFiles(newFiles);
  };

  // Function to add a new row for uploading a new file
  const addNewFileInput = () => {
    setFiles([...files, { file: null, columns: [], selectedColumns: [], jsonData: [] }]);
  };

  // Function to handle the selection of columns
  const handleColumnSelect = (selectedOptions, index) => {
    const newFiles = [...files];
    newFiles[index].selectedColumns = selectedOptions;
    setFiles(newFiles);
  };

  // Function to check if two rows match based on selected columns
  const isRowMatch = (row1, row2, selectedColumns) => {
    return selectedColumns.every((col) => row1[col] === row2[col]);
  };

  // Function to merge rows from two files based on selected columns
  const mergeRowsBySelectedColumns = (file1, file2, selectedColumns) => {
    const mergedRows = [];

    file1.forEach((row1) => {
      // Find all matching rows from file2
      const matchingRows = file2.filter((row2) => isRowMatch(row1, row2, selectedColumns));

      if (matchingRows.length > 0) {
        // For each matching row, create a new merged row
        matchingRows.forEach((row2) => {
          const mergedRow = { ...row1, ...row2 }; // Merge row1 and row2 into a single row object
          mergedRows.push(mergedRow);
        });
      } else {
        mergedRows.push({ ...row1 }); // If no matching rows, just push the original row1
      }
    });

    return mergedRows;
  };

  // Function to handle the form submit and merge the files
  const handleMergeFiles = (files, selectedColumns) => {
    if (files.length < 2) {
      console.log("You need at least two files to perform merging.");
      return;
    }

    const file1Data = files[0].jsonData; // Data from the first file
    const file2Data = files[1].jsonData; // Data from the second file

    const mergedData = mergeRowsBySelectedColumns(file1Data, file2Data, selectedColumns);

    return mergedData;
  };

  // Function to export the merged data to an Excel file
  const exportToExcel = (mergedData) => {
    const worksheet = XLSX.utils.json_to_sheet(mergedData); // Create a worksheet from merged data
    const workbook = XLSX.utils.book_new(); // Create a new workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Merged Data"); // Append the worksheet to the workbook
    XLSX.writeFile(workbook, "merged_data.xlsx"); // Trigger download of the Excel file
  };

  // Function to handle the submit action
  const handleSubmit = () => {
    const selectedColumns = files[0].selectedColumns.map(col => col.value); // Get the selected columns from the first file
    const mergedData = handleMergeFiles(files, selectedColumns);

    if (mergedData) {
      exportToExcel(mergedData); // Export the merged data to Excel
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Excel File Uploader
      </Typography>
      <form onSubmit={(e) => e.preventDefault()}>
        {files.map((fileData, index) => (
          <Grid container spacing={3} key={index} alignItems="flex-start" sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="file"
                inputProps={{ accept: ".xlsx, .xls" }}
                onChange={(e) => handleFileUpload(e, index)}
                variant="outlined"
                label={`Upload Excel File ${index + 1}`}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                isMulti
                options={fileData.columns}
                value={fileData.selectedColumns}
                onChange={(selectedOptions) => handleColumnSelect(selectedOptions, index)}
                placeholder="Select columns"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 65, // Align with TextField
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999, // Ensure the dropdown appears above other elements
                  }),
                }}
                menuPlacement="auto" // Auto places menu (can use "bottom" to force)
                menuPosition="absolute" // Makes sure the menu is aligned to the input
              />
            </Grid>
          </Grid>
        ))}

        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={addNewFileInput}
          >
            Add another file
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export default ExcelUploader;
